import { useCallback, useMemo, useRef, useState } from "react";
import { Database, FlaskConical, Gauge, History, Play, ShieldCheck, Terminal, Users } from "lucide-react";
import { ADMIN_ROLES } from "@hrms/types";

import { usePageBack } from "../../context/PageBackContext.js";
import { useAuth } from "../../hooks/useAuth.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import { Textarea } from "../../components/ui/textarea.js";
import { Button } from "../../components/ui/button.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Progress } from "../../components/ui/progress.js";
import { ConsoleOutput, type ConsoleLine, type ConsoleTone } from "../../components/ui/console-output.js";
import { SummaryStatCard, SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { generateSource, type SourceSettings } from "../../lib/migration-sim/source.js";
import { selectEmployees, validateFilters } from "../../lib/migration-sim/selection.js";
import { emptyWorld, loadWorld, migratedBayIds, progress, saveWorld, type SimWorld } from "../../lib/migration-sim/state.js";
import { PACKAGE, parseCommand, runCommand, type SimIo } from "../../lib/migration-sim/engine.js";
import { commandToDraft, defaultDraft, draftToCommand, type Draft } from "../../lib/migration-sim/draft.js";
import { FilterPanel } from "./migration-sim/FilterPanel.js";
import { HrmsPanel, MemoryPanel, ProgressPanel, RunsPanel, SettingsPanel, SourcePanel, type Speed } from "./migration-sim/SimPanels.js";

function decodeRole(token: string): string {
  try {
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["role"] ?? "");
  } catch { return ""; }
}

const SPEED: Record<Speed, number> = { instant: 0, normal: 1, slow: 2.5 };
const QUICK: Array<[string, string]> = [
  ["status", "status"], ["history", "history"], ["show last", "show last"],
  ["modules (last run)", "migrate:modules --batch=last"], ["verify (last run)", "verify --batch=last"],
  ["reset memory", "reset"], ["help", "help"],
];

/**
 * Admin > Migration v2 Simulator: the legacy migration tool (tools/migration-v2) as a dashboard,
 * for showing and trying out batch migrations. The commands are the tool's own and run in the
 * browser on synthetic employees (lib/migration-sim): nothing reaches the API, the legacy
 * database or HRMS, and the state lives in this browser only.
 */
export default function MigrationSimulatorPage() {
  usePageBack("/admin", "Back to Admin");
  const { accessToken } = useAuth();
  const allowed = ADMIN_ROLES.has(accessToken ? decodeRole(accessToken) : "");
  const confirm = useConfirm();

  const [world, setWorld] = useState<SimWorld>(loadWorld);
  const [draft, setDraft] = useState<Draft>(defaultDraft);
  const [command, setCommand] = useState(() => draftToCommand(defaultDraft));
  const [commandError, setCommandError] = useState<string | null>(null);
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [bar, setBar] = useState<{ label: string; percent: number } | null>(null);
  const [speed, setSpeed] = useState<Speed>("normal");
  const [tab, setTab] = useState("runs");
  const lineId = useRef(0);
  const past = useRef<string[]>([]);
  const pastAt = useRef(-1);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const { size, seed, failureRate } = world.settings;
  const source = useMemo(() => generateSource({ size, seed, failureRate }), [size, seed, failureRate]);
  const migrated = useMemo(() => migratedBayIds(world.memory), [world.memory]);
  const p = progress(world.memory);
  const inHrms = Object.keys(world.hrms.employees).length;
  const lastRun = world.memory.runs[world.memory.runs.length - 1];

  // What the current filters would pick, recomputed as they change.
  const live = useMemo(() => {
    try {
      validateFilters(draft.filters, source);
      const f = draft.filters.codesFile
        ? { ...draft.filters, codes: [...(draft.filters.codes ?? []), ...(world.files[draft.filters.codesFile] ?? "").split(/[\r\n,;\t]+/).map((s) => s.trim()).filter(Boolean)] }
        : draft.filters;
      const sel = selectEmployees(source, f, migrated);
      return { sel, error: null };
    } catch (e) {
      return { sel: null, error: (e as Error).message };
    }
  }, [draft.filters, source, migrated, world.files]);
  const pickedNow = useMemo(() => new Set(live.sel?.picked.map((m) => m.legacyBayUserId) ?? []), [live.sel]);

  const print = useCallback((text: string, tone?: ConsoleTone) => {
    setLines((ls) => [...ls, { id: ++lineId.current, text, tone }]);
  }, []);

  const changeDraft = (d: Draft) => {
    setDraft(d);
    setCommand(draftToCommand(d));
    setCommandError(null);
  };

  // Typing in the command box updates the filter panel when it's a preview / migrate line.
  const changeCommand = (text: string) => {
    setCommand(text);
    try {
      const d = commandToDraft(text);
      if (d) setDraft(d);
      setCommandError(null);
    } catch (e) {
      setCommandError((e as Error).message);
    }
  };

  const execute = useCallback(async (line: string): Promise<number> => {
    const text = line.trim();
    if (!text || busy) return 0;
    if (past.current[past.current.length - 1] !== text) past.current.push(text);
    pastAt.current = -1;
    if (text === "clear" || text === "cls") { setLines([]); return 0; }

    setBusy(text);
    print(text, "cmd");
    const work: SimWorld = structuredClone(world);
    const factor = SPEED[speed];
    const io: SimIo = {
      print,
      progress: setBar,
      sleep: (ms) => new Promise((r) => setTimeout(r, ms * factor)),
      confirm: ({ title, description, actionText, destructive }) => confirm({ title, description, actionText, tone: destructive ? "destructive" : "default" }),
      panelFilters: () => draftRef.current.filters,
      commit: () => { saveWorld(work); setWorld(structuredClone(work)); },
    };
    try {
      const code = await runCommand(text, work, io);
      io.commit();
      return code;
    } finally {
      setBar(null);
      setBusy(null);
    }
  }, [busy, world, speed, print, confirm]);

  // Buttons elsewhere on the page show their command in the box, then run it.
  const runShown = useCallback((line: string) => {
    setCommand(line);
    void execute(line);
  }, [execute]);

  const runBox = async () => {
    const code = await execute(command);
    if (code !== 0) throw new Error(`exit ${code}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      runBox().catch(() => {});
      return;
    }
    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !command.includes("\n") && past.current.length) {
      e.preventDefault();
      const n = past.current.length;
      const at = pastAt.current === -1 ? n : pastAt.current;
      const next = e.key === "ArrowUp" ? Math.max(0, at - 1) : Math.min(n, at + 1);
      pastAt.current = next === n ? -1 : next;
      changeCommand(next === n ? draftToCommand(draft) : past.current[next]!);
    }
  };

  const startOver = async (settings: SourceSettings) => {
    const ok = await confirm({
      title: "Use the new source and start over?",
      description: "The simulated runs, memory archives and pretend HRMS rows are cleared, since they point at people the new source doesn't have.",
      actionText: "Start over",
      tone: "warning",
    });
    if (!ok) return;
    const w = { ...emptyWorld(), settings, files: world.files };
    saveWorld(w);
    setWorld(w);
    setLines([]);
    changeDraft(defaultDraft);
  };

  const wipe = async () => {
    const ok = await confirm({
      title: "Wipe the simulator?",
      description: "Forgets every simulated run, archive, uploaded codes file and pretend HRMS row in this browser. Nothing real is affected.",
      actionText: "Wipe",
      tone: "destructive",
    });
    if (!ok) return;
    const w = emptyWorld();
    saveWorld(w);
    setWorld(w);
    setLines([]);
    changeDraft(defaultDraft);
  };

  if (!allowed) return <div className="p-8 text-sm text-surface-600">Access denied. Admins only.</div>;

  const lastVerify = lastRun?.verify?.summary;
  const verifyBad = lastVerify ? lastVerify.leave.mismatch + lastVerify.attendance.mismatch + (lastVerify.employees?.missing.length ?? 0) : 0;
  const parsedScript = (() => { try { return parseCommand(command).script; } catch { return ""; } })();

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-surface-900">Migration v2 Simulator</h1>
          <p className="text-sm text-surface-600">Batch migration from the legacy team site, exactly as <span className="font-mono">{PACKAGE}</span> runs it: pick employees, preview, migrate, verify, track progress, reset.</p>
        </div>
        <Badge tone="accent" className="text-xs"><FlaskConical className="h-3.5 w-3.5" /> Simulation · synthetic data</Badge>
      </div>

      <Card className="border-[var(--color-info-border)] bg-[var(--color-info-bg)]">
        <CardContent className="flex items-start gap-3 p-4 text-sm text-[var(--color-info-fg)]">
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Every command here runs in your browser against {source.length} generated employees. Nothing touches the legacy database, the API or HRMS, and the state is kept in this browser only. The real tool lives in <span className="font-mono">tools/migration-v2</span>; its USER-GUIDE.md lists the same commands.</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryStatCard icon={Gauge} tone="brand" label="Progress" value={<>{p.percent}<SummaryStatSuffix>%</SummaryStatSuffix></>} caption={world.memory.source ? `${p.migrated} of ${p.total} employees` : "no run yet"} />
        <SummaryStatCard icon={Users} tone="warning" label="Still to migrate" value={world.memory.source ? p.total - p.migrated : source.length} caption="not loaded by a real run" />
        <SummaryStatCard icon={History} tone="info" label="Runs" value={<>{world.memory.runs.filter((r) => !r.dryRun).length}<SummaryStatSuffix> real</SummaryStatSuffix></>} caption={`${world.memory.runs.filter((r) => r.dryRun).length} dry · ${world.archives.length} archived memories`} />
        <SummaryStatCard icon={Database} tone="success" label="In HRMS (simulated)" value={inHrms} caption={inHrms !== p.migrated ? "differs from the memory: see HRMS tab" : "matches the memory"} />
        <SummaryStatCard
          icon={ShieldCheck}
          tone={!lastVerify ? "accent" : verifyBad ? "danger" : "success"}
          label="Last run verified"
          value={!lastRun ? "—" : !lastVerify ? "Not yet" : verifyBad ? `${verifyBad} issues` : "All match"}
          valueClassName="text-base"
          caption={lastRun ? lastRun.id : "no run yet"}
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,460px)_minmax(0,1fr)]">
        <FilterPanel
          draft={draft}
          onChange={changeDraft}
          source={source}
          migrated={migrated}
          files={world.files}
          onUploadCodes={(name, text) => setWorld((w) => { const next = { ...w, files: { ...w.files, [name]: text } }; saveWorld(next); return next; })}
          preview={live.sel ? { matched: live.sel.matched.length, already: live.sel.alreadyMigrated, picked: live.sel.picked.length, managers: live.sel.managersAdded, unknown: live.sel.unknownCodes } : null}
          previewError={live.error}
          disabled={!!busy}
        />

        <Card className="xl:sticky xl:top-20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Terminal className="h-4 w-4" /> Command</CardTitle>
            <CardDescription>The filters write this command; edit it here and the filters follow. Enter runs it, Shift+Enter adds a line, ↑ / ↓ recall earlier commands.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Textarea
              value={command}
              onChange={(e) => changeCommand(e.target.value)}
              onKeyDown={onKeyDown}
              autoGrow
              minRows={2}
              maxRows={6}
              spellCheck={false}
              aria-label="Migration command"
              tone={commandError ? "error" : "default"}
              className="font-mono text-[12.5px]"
            />
            {commandError && <p className="text-xs text-[var(--color-error-fg)]">{commandError}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <AsyncButton onClick={() => runBox()} disabled={!!busy} variant={parsedScript === "reset:db" || draft.reset ? "destructive" : "default"} {...(bar ? { progress: bar.percent } : {})}>
                <Play className="h-4 w-4" /> Run
              </AsyncButton>
              <Button variant="secondary" size="sm" disabled={!!busy} onClick={() => changeDraft(draft)}>Rebuild from filters</Button>
              <span className="mx-1 h-5 w-px bg-surface-200" aria-hidden />
              {QUICK.map(([label, cmd]) => (
                <Button key={label} variant="ghost" size="sm" disabled={!!busy} onClick={() => runShown(`pnpm --filter ${PACKAGE} ${cmd}`)}>{label}</Button>
              ))}
              <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => setLines([])}>clear</Button>
            </div>
            {busy && (
              <div className="flex items-center gap-2 text-xs text-surface-600">
                <Badge tone="info">running</Badge>
                <span className="truncate font-mono">{busy}</span>
              </div>
            )}
            <ConsoleOutput
              lines={lines}
              className="h-[520px]"
              placeholder={<>Output appears here. Try <span className="text-slate-300">help</span>, or press Run to preview the current filters.</>}
              footer={bar && (
                <div className="mt-2 flex items-center gap-3 text-slate-300">
                  <Progress value={bar.percent} className="h-1.5 flex-1" />
                  <span className="shrink-0 tabular-nums">{bar.percent}% · {bar.label}</span>
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="runs">Runs ({world.memory.runs.length})</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="source">Source data</TabsTrigger>
          <TabsTrigger value="hrms">HRMS (simulated)</TabsTrigger>
          <TabsTrigger value="memory">Memory file</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="runs"><RunsPanel world={world} run={runShown} busy={!!busy} /></TabsContent>
        <TabsContent value="progress"><ProgressPanel world={world} /></TabsContent>
        <TabsContent value="source"><SourcePanel world={world} source={source} picked={pickedNow} /></TabsContent>
        <TabsContent value="hrms"><HrmsPanel hrms={world.hrms} source={source} run={runShown} busy={!!busy} /></TabsContent>
        <TabsContent value="memory"><MemoryPanel world={world} /></TabsContent>
        <TabsContent value="settings">
          <SettingsPanel key={`${size}-${seed}-${failureRate}`} settings={world.settings} speed={speed} onSpeed={setSpeed} onApply={(s) => void startOver(s)} onWipe={() => void wipe()} busy={!!busy} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
